import React from 'react'
import { HTMLText } from '@crossfit/ui'

import { useUnitPreference } from '../UnitPreference'

const INCH_LENGTHS = {
  6: 15,
  12: 30,
  16: 41,
  20: 51,
  24: 61,
  30: 76,
  36: 91,
  40: 102,
  44: 112,
  52: 132,
}

const FEET_LENGTHS = {
  10: 3,
  12: 3.6,
  15: 4.6,
}

const BALL_LOADS = {
  4: 2,
  6: 2.7,
  8: 3.6,
  10: 4,
  12: 5,
  14: 6,
  16: 7,
  18: 8,
  20: 9,
  25: 11,
  30: 13,
  40: 17.5,
  50: 22.5,
}

const SANDBAG_LOADS = {
  ...BALL_LOADS,
  35: 16,
}

const KETTLEBELL_LOADS = {
  9: 4,
  13: 6,
  18: 8,
  26: 12,
  35: 16,
  40: 18,
  44: 20,
  53: 24,
  62: 28,
  70: 32,
  80: 36,
  88: 40,
  97: 44,
  106: 48,
  124: 56,
  150: 68,
  176: 80,
  203: 92,
}

const DUMBBELL_LOADS = {
  5: 2.5,
  10: 5,
  15: 7.5,
  20: 10,
  25: 12.5,
  30: 12.5,
  35: 15,
  40: 17.5,
  45: 20,
  50: 22.5,
  55: 25,
  60: 27.5,
  65: 30,
  70: 32.5,
  75: 35,
  80: 37.5,
  85: 37.5,
  90: 40,
  95: 42.5,
  100: 45,
  110: 50,
  115: 52.5,
  120: 55,
  130: 60,
  140: 62.5,
  150: 67.5,
}

const BARBELL_LOADS = {
  15: 5,
  25: 10,
  35: 15,
  45: 20,
  55: 25,
  65: 29,
  75: 34,
  80: 36,
  85: 38,
  95: 43,
  105: 47,
  110: 50,
  115: 52,
  125: 56,
  135: 61,
  145: 65,
  155: 70,
  165: 75,
  175: 79,
  185: 84,
  195: 88,
  205: 93,
  215: 97,
  225: 102,
  235: 106,
  245: 111,
  255: 115,
  265: 120,
  275: 125,
  285: 129,
  295: 134,
  305: 138,
  315: 143,
  325: 147,
  335: 152,
  345: 156,
  355: 161,
  365: 165,
  375: 170,
  385: 175,
  395: 179,
  405: 184,
  425: 193,
  435: 198,
  445: 202,
  455: 207,
  465: 211,
  485: 220,
  505: 230,
  525: 239,
  545: 248,
  555: 252,
  565: 256,
  585: 266,
  605: 275,
}

function feetToMeters(feet: number) {
  return parseFloat((feet / 3.281).toFixed(1))
}

function convertAllUnitsInText(
  text: string,
  conversions: Record<number, number>,
) {
  const canBeReplaced = text
    .match(/\d+/g)
    .every((length) => conversions[length])

  if (!canBeReplaced) {
    throw new Error(`${text} cannot be converted`)
  }

  return text.replace(/\d+/g, (length: string) => conversions[length])
}

function convertAllFeetInText(text: string) {
  return text.replace(/\d+/g, (length: string) => {
    if (FEET_LENGTHS[length]) {
      return FEET_LENGTHS[length]
    } else {
      return feetToMeters(Number(length))
    }
  })
}

export function convertWorkoutTextToMetric(text: string) {
  return text
    .split(/\n/)
    .map((line) =>
      line
        .replace(/\(\d+\/\d+\s*(lb|#)\)/, (text: string) => {
          // Converts (135/95 lb) to (61/43 kg)

          const isDumbbell = /db\s/i.exec(line)
          const isKettlebell = /kb\s/i.exec(line)
          const isBall = /ball(s?)\s/i.exec(line)
          const isSandbag = /sandbag(s?)\s/i.exec(line)

          try {
            let conversions: Record<number, number> = BARBELL_LOADS

            if (isDumbbell) {
              conversions = DUMBBELL_LOADS
            } else if (isKettlebell) {
              conversions = KETTLEBELL_LOADS
            } else if (isBall) {
              conversions = BALL_LOADS
            } else if (isSandbag) {
              conversions = SANDBAG_LOADS
            }

            return convertAllUnitsInText(text, conversions).replace(
              /#|lb/i,
              'kg',
            )
          } catch (error) {
            console.error(error)
            return text
          }
        })
        .replace(/\(\d+\/\d+\s*in\)/, (text: string) => {
          // Converts (24/20 in) to (61/51 cm)

          try {
            return convertAllUnitsInText(text, INCH_LENGTHS).replace(
              /in/i,
              'cm',
            )
          } catch (error) {
            console.error(error)
            return text
          }
        })
        .replace(/\(\d+\/?\d+\s*ft\)/, (text: string) => {
          // Converts (12/15 ft) to (3.7/4.6 m)

          return convertAllFeetInText(text).replace(/ft/i, 'm')
        })
        .replace(/\d+-ft./, (text: string) => {
          // Converts 100-ft. to 30-m.

          return text
            .replace(/\d+/g, (distance) =>
              feetToMeters(Number(distance)).toString(),
            )
            .replace(/ft/i, 'm')
        })
        .replace(/yard/gi, 'meter'),
    )
    .join('\n')
}

function WorkoutText({ children, ...rest }) {
  const { unitPreference } = useUnitPreference()

  return (
    <HTMLText {...rest}>
      {unitPreference ? convertWorkoutTextToMetric(children) : children}
    </HTMLText>
  )
}

export default WorkoutText
